/**
 * Calculates the amount of energy that was both imported (P+) and exported (P-)
 * within the exact same 15-minute interval. This represents energy that could 
 * have been directly self-consumed if phases were balanced or loads were shifted.
 *
 * @param {Array} data - Array of parsed CSV records { datetime, type, kwh }
 * @returns {Object} { totalImbalance, dailyData }
 */
export function calculateImbalance(data) {
  if (!data || data.length === 0) {
    return { totalImbalance: 0, dailyData: [] }
  }

  const intervals = {}
  
  data.forEach(record => {
    if (!intervals[record.datetime]) {
      intervals[record.datetime] = { purchase: 0, sale: 0 }
    }
    if (record.type === 'P+') {
      intervals[record.datetime].purchase += record.kwh
    } else if (record.type === 'P-') {
      intervals[record.datetime].sale += record.kwh
    }
  })

  let totalImbalance = 0
  const dailyImbalance = {}

  Object.entries(intervals).forEach(([dt, values]) => {
    const { purchase, sale } = values
    const imbalance = Math.min(purchase, sale)
    
    if (imbalance > 0) {
      totalImbalance += imbalance
      
      const dateStr = dt.substring(0, 10) // "YYYY-MM-DD"
      if (!dailyImbalance[dateStr]) {
        dailyImbalance[dateStr] = 0
      }
      dailyImbalance[dateStr] += imbalance
    }
  })

  // Ensure all dates from the range are included, even with 0 imbalance
  const dates = Object.keys(intervals).map(dt => dt.substring(0, 10))
  if (dates.length > 0) {
    const minDate = new Date(Math.min(...dates.map(d => new Date(d))))
    const maxDate = new Date(Math.max(...dates.map(d => new Date(d))))
    
    for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + 1)) {
        const dStr = d.toISOString().substring(0, 10)
        if (dailyImbalance[dStr] === undefined) {
            dailyImbalance[dStr] = 0
        }
    }
  }

  const sortedDates = Object.keys(dailyImbalance).sort()
  const dailyData = sortedDates.map(date => ({
    date,
    imbalance: dailyImbalance[date]
  }))

  return {
    totalImbalance,
    dailyData,
  }
}
